import * as React from 'react';
import { Platform, View } from 'react-native';
import { SafeAreaInsetsContext, useSafeAreaInsets } from 'react-native-safe-area-context';

import { BRAND } from '@sixam/config/brand';

/**
 * Reserves a solid dark strip for the Android status bar and moves the whole app
 * below it, so the clock, battery, and notification icons never sit on top of
 * app content.
 *
 * The height is the real safe-area inset, not a fixed number: status bars are
 * around 24dp on most phones and taller on devices with a notch or punch-hole,
 * so a hardcoded value clips the icons on some handsets and leaves a gap on
 * others.
 *
 * Descendants are given a top inset of zero on purpose. The strip has already
 * consumed that space, so screens that pad by `insets.top` themselves — the
 * member header does — would otherwise reserve it a second time and push their
 * content down twice.
 */
export function AndroidStatusBarInset({ children }: Readonly<{ children: React.ReactNode }>) {
  const insets = useSafeAreaInsets();
  const consumedInsets = React.useMemo(() => ({ ...insets, top: 0 }), [insets]);

  if (Platform.OS !== 'android') {
    return <>{children}</>;
  }

  return (
    <View style={{ backgroundColor: BRAND.color.ink, flex: 1, paddingTop: insets.top }}>
      <SafeAreaInsetsContext.Provider value={consumedInsets}>
        {children}
      </SafeAreaInsetsContext.Provider>
    </View>
  );
}
